/**
 * ArcHistoryTimeline - Display arc transition history
 * Shows a chronological timeline of stage transitions with user attribution
 */

import React, { useMemo } from 'react';
import { Sphere } from 'types/global';
import { ArcHistory, ArcTemplate } from 'types/arc';
import { useTranslation } from 'localization';
import { Timeline } from 'primereact/timeline';
import TimelineMarkerAtom from '../../atoms/TimelineMarker/TimelineMarkerAtom';
import TimelineEventCardMolecule from '../TimelineEventCard/TimelineEventCardMolecule';
import './ArcHistoryTimelineMolecule.scss';

export interface ArcHistoryTimelineProps {
  arcSphere: Sphere;
  arcTemplate: ArcTemplate;
  limit?: number;
  showComments?: boolean;
}

interface EnrichedArcHistory extends ArcHistory {
  stageName: string;
  actionLabel: string;
}

const ArcHistoryTimelineMolecule: React.FC<ArcHistoryTimelineProps> = ({
  arcSphere,
  arcTemplate,
  limit,
  showComments = true,
}) => {
  const { t } = useTranslation();

  // Extract and enrich history from arcSphere.properties.history
  const history = useMemo(() => {
    const rawHistory: ArcHistory[] = Array.isArray(arcSphere.properties.history)
      ? arcSphere.properties.history
      : [];

    // Enrich history with stage names and action labels
    const enriched: EnrichedArcHistory[] = rawHistory.map((entry) => {
      const stageDef = arcTemplate.definition.stages.find(
        (s) => s.id === entry.stageId
      );

      const stageName = stageDef
        ? t(stageDef.localizationKey, stageDef.fallbackName)
        : entry.stageId;

      // Determine action label based on status transition
      let actionLabel: string;
      if (entry.status === 'active') {
        actionLabel = t('arc.history.action_started');
      } else if (entry.status === 'completed') {
        actionLabel = t('arc.history.action_completed');
      } else {
        // `ArcHistory` carries one `status` and no previous value, so this label
        // states that the stage changed without naming a from/to pair it cannot know.
        actionLabel = t('arc.history.action_changed');
      }

      return {
        ...entry,
        stageName,
        actionLabel,
      };
    });

    // Sort by timestamp descending (most recent first)
    enriched.sort(
      (a: EnrichedArcHistory, b: EnrichedArcHistory) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );

    // Apply limit if specified
    return limit ? enriched.slice(0, limit) : enriched;
  }, [arcSphere.properties.history, arcTemplate, limit, t]);

  const customizedMarker = (item: EnrichedArcHistory) => {
    return <TimelineMarkerAtom status={item.status} />;
  };

  const customizedContent = (item: EnrichedArcHistory) => {
    return (
      <TimelineEventCardMolecule
        stageName={item.stageName}
        actionLabel={item.actionLabel}
        timestamp={item.timestamp}
        updatedBy={item.updatedBy}
        comment={item.comment}
        showComment={showComments}
      />
    );
  };

  return (
    <div className="arc-history-timeline">
      <div className="timeline-title">
        <h3>{t('arc.history.title')}</h3>
        {history.length > 0 && (
          <span className="timeline-count">
            {t('arc.history.events_count', {
              count: history.length,
            })}
          </span>
        )}
      </div>

      {history.length === 0 && (
        <div className="timeline-empty">
          <i className="pi pi-info-circle" />
          <span>{t('arc.history.no_history')}</span>
        </div>
      )}

      {history.length > 0 && (
        <Timeline
          value={history}
          content={customizedContent}
          marker={customizedMarker}
          className="arc-timeline"
        />
      )}
    </div>
  );
};

export default ArcHistoryTimelineMolecule;
